import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IColumnMetaData, IFilterGroup, IFilterOption, IReportFiltersOption, IReportMetadata, ReportDataService } from '@app/shared/report/report-data.service';
import { IObject } from '@type_definitions/datatypes';
import { FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { LovService } from '@app/shared/services/lov.service';
import _ from 'underscore';
import { UntypedFormGroup } from '@angular/forms';
import { ReportFiltersUtil, SCREENTYPE_DATERANGE, SCREENTYPE_LIST, SCREENTYPE_LIST_MULTIPLE, SCREENTYPE_SEARCH, TYPE_DATE, TYPE_NUMBER } from '@app/shared/report/report-filters-util.service';
import { util } from '@app/shared/util';

const DEFAULT_LABEL_CLASS: string = 'col-xsm-3 col-sm-3 col-md-4 col-lg-4';
const DEFAULT_VALUE_CLASS: string = 'col-xsm-9 col-sm-9 col-md-8 col-lg-8';
const DEFAULT_VALUE_CLASS_DATE: string = 'col-xsm-5 col-sm-4 col-md-6 col-lg-4';

@Component({
  selector: 'report-filters',
  template: `
    <div *ngIf="reportMetadata">
      <div *ngIf="!hasFilterGroups">
        <formly [form]="form" [fields]="fields" [model]="model" class="report-filters" [noValidate]="true">
          <button [hidden]="true" (click)="onSubmit.emit()" translate>GA.BUTTONS.SEARCH</button>
        </formly>
      </div>

      <div *ngIf="hasFilterGroups">
        <report-filters-collapsible [form]="form" [fields]="fields" [model]="model" [groups]="reportMetadata.filterGroups" (onCollapsedChange)="onGroupCollapsedChange($event)">
          <ng-template #searchButton>
            <button [hidden]="true" (click)="onSubmit.emit()" translate>GA.BUTTONS.SEARCH</button>
          </ng-template>
        </report-filters-collapsible>
      </div>
    </div>
  `
})
export class ReportFiltersComponent implements OnInit, OnChanges {
  @Input() public reportMetadata:IReportMetadata;
  @Input() public model:IObject;
  @Input() public labelClass: string;
  @Input() public valueClass: string;
  @Input() public valueClassDate: string;
  @Input() public showFilters:boolean;

  @Output() public onFilterChange: EventEmitter<IObject> = new EventEmitter<IObject>();
  @Output() public onSubmit:EventEmitter<void> = new EventEmitter();

  public form: UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[] | IObject<IExtraFieldConfigurationObject[]>;
  private multiListFields: string[] = [];
  private multiListTimeout:number;

  constructor(
    private formlyService:GaFormlyService,
    private lovService:LovService,
    private reportDataService:ReportDataService,
    private reportFiltersUtil:ReportFiltersUtil
  ) {
    /**/
  }

  public get hasFilterGroups():boolean {
    return !!this.reportMetadata.filterGroups?.length;
  }

  public ngOnInit():void {
    this.labelClass = this.labelClass || DEFAULT_LABEL_CLASS;
    this.valueClass = this.valueClass || DEFAULT_VALUE_CLASS;
    this.valueClassDate = this.valueClassDate || DEFAULT_VALUE_CLASS_DATE;
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.showFilters) {
      this.showFilters = changes.showFilters.currentValue;

      // we only trigger change when it is not the first change
      if (!changes.showFilters.isFirstChange()) {
        this.onShowFiltersChange();
      }
    }

    if (changes.model) {
      this.onModelChange(changes.model.currentValue, changes.model.previousValue);
    }

    if (changes.reportMetadata) {
      this.onReportMetadataChange(changes.reportMetadata.currentValue);
    }
  }

  private onModelChange(newModel:IObject, previousModel:IObject):void {
    // trigger changes when there are real changes
    if (!_.isEqual(newModel, previousModel)) {
      this.model = this.processModel(newModel);
      this.onFilterChange.emit(this.model);

      // when 'filterModel' input updated outside component
      // make sure to update the formControl value
      if (!_.isEmpty(this.form.controls)) {
        this.form.patchValue(this.model);
      }

      // if there are multi list fields then should always format model for multi list fields
      if (!_.isEmpty(this.multiListFields)) {
        clearTimeout(this.multiListTimeout);
        // add timeout because formControl might still undefined because form is not fully rendered in this early lifecycle hook
        this.multiListTimeout = setTimeout(() => {
          _.each(this.multiListFields, (key:string) => {
            if(this.model[key] && !_.isArray(this.model[key])) {
              // initial model multilist value would be e.g. '123,456', formly multiselect expect ['123','456']}
              this.form.controls[key].setValue(this.model[key].split(','));}
          });
        });
      }
    }
  }

  private onReportMetadataChange(reportMetadata:IReportMetadata):void {
    this.multiListFields = [];

    if (reportMetadata?.columns?.length) {
      const expandedGroupCodes:string[] = [];
      // if there is filter groups then create filter fields based on group code
      if (this.hasFilterGroups) {
        this.fields = {};

        _.each(reportMetadata.filterGroups, (group:IFilterGroup) => {
          // filter only defined in group columns
          const clonedFilterData = util.deepClone(reportMetadata);
          clonedFilterData.columns = _.filter(clonedFilterData.columns, (col:IColumnMetaData) => group.columns.indexOf(col.name) > -1);
          this.fields[group.code] = this.createFilters(clonedFilterData);

          // find expanded group codes initially
          if (!group.collapsed && this.showFilters) {
            expandedGroupCodes.push(group.code);
            // mark this group filters as loaded
            group.loaded = true;
          }
        });
      } else {
        // regular filter field
        this.fields = this.createFilters(reportMetadata);
      }

      if (this.showFilters) {
        // fetch filter list items
        this.fillFields(expandedGroupCodes);
      }
    }
  }

  private onShowFiltersChange():void {
    const expandedGroupCodes:string[] = [];

    _.each(this.reportMetadata.filterGroups, (group:IFilterGroup) => {
      if (!group.collapsed && !group.loaded) {
        group.loaded = true;
        expandedGroupCodes.push(group.code);
      }
    });

    if (this.showFilters) {
      // fetch filter list items
      this.fillFields(expandedGroupCodes);
    }
  }

  public onGroupCollapsedChange(group:IFilterGroup):void {
    // When the group filter is expanded, we will fetch the filter list items only for the filters that do not contain all domain filters
    // as domain filters are already preloaded at the start
    if (!group.loaded && this.validateGroupFilters(group.code)) {
      // mark this filter group as loaded
      group.loaded = true;
      this.fillFields([group.code]);
    }
  }

  private processModel(model:IObject):IObject {
    const preparedModel:IObject = {};

    _.each(model, (val:string|number, key:string) => {
      if (util.isNullOrUndefinedOrEmpty(val)) {
        preparedModel[key] = undefined;
      } else {
        preparedModel[key] = val;
      }
    });

    return preparedModel;
  }

  private createFilters(filterData: IReportMetadata): IExtraFieldConfigurationObject[] {
    const filterFields:IExtraFieldConfigurationObject[] = [];

    const columns: IColumnMetaData[] = _.sortBy(filterData.columns, (c:IColumnMetaData) => c.searchSequence);

    _.each(columns, (filter:IColumnMetaData) => {
      if (filter.formatting.showinsearch) {
        let filterField: IExtraFieldConfigurationObject;

        if (filter.screenType === SCREENTYPE_DATERANGE) {
          filterField = this.createDateRangeFilter(filter);
        } else if (filter.filterScreenType === SCREENTYPE_LIST || filter.screenType === SCREENTYPE_LIST) {
          filterField = this.createListFilter(filter, false);
        } else if (filter.filterScreenType === SCREENTYPE_LIST_MULTIPLE || filter.screenType === SCREENTYPE_LIST_MULTIPLE) {
          this.multiListFields.push(filter.name); // we collect all multifields to a list
          filterField = this.createListFilter(filter, true);
        } else if (filter.filterScreenType === SCREENTYPE_SEARCH || filter.screenType === SCREENTYPE_SEARCH) {
          filterField = this.createSearchFilter(filterData.id, filter, this.model[filter.name]);
        } else if (filter.type === TYPE_DATE) {
          filterField = this.createDateFilter(filter);
        } else if (filter.type === TYPE_NUMBER) {
          filterField = this.createNumberFilter(filter);
        } else {
          filterField = this.createTextFilter(filter);
        }

        filterFields.push(filterField);
      }
    });

    return filterFields;
  }

  private createDateFilter(filter:IColumnMetaData):IExtraFieldConfigurationObject {
    return this.formlyService.aCombinedDate(filter.name, this.formatLabel(filter))
      .withLabelClass(this.labelClass)
      .withValueClass('col-xsm-9 col-sm-6 col-md-6')
      .withWrapperClass('col-md-6')
      .build();
  }

  private createDateRangeFilter(filter:IColumnMetaData): IExtraFieldConfigurationObject {
    return this.reportFiltersUtil.createDateRangeFilterFields(this.formlyService, this.formatLabel(filter), filter.name, undefined, this.labelClass, this.valueClass);
  }

  private createNumberFilter(filter:IColumnMetaData):IExtraFieldConfigurationObject {
    // matches a positive or negative number, possibly preceded by: < > <= >= != !< !>
    const re1:RegExp = /^((!?[<>]|[<>]=|!=)(\s+)?(-)?(\s+)?)?[0-9]+$/;
    // matches multiple numbers, separated by commas
    const re2:RegExp = /^([0-9]+(\s*,\s*))+?[0-9]+$/;
    // matches multiple numbers, separated by commas, inside a 'not in ( )'
    const re3:RegExp = /^not\s+in\s+\((\s+)?([0-9]+(\s*,\s*)?)+[0-9]+(\s+)?\)$/;
    // matches 'null', 'is null' and 'is not null'
    const re4:RegExp = /^(is\s+(not\s+)?)?null$/;
    // matches 'between 12345 and 12346'
    const re5:RegExp = /^between\s+[0-9]+\s+and\s+[0-9]+$/;
    // matches a number containing decimals
    const re6:RegExp = /^\d+\.\d{1,4}$/;
    // matches multiple numbers, separated by spaces
    const re7: RegExp = /^[\d\s]+$/;
    const reOR:RegExp = /|/;
    const validateRegex:RegExp = new RegExp(re1.source + reOR.source + re2.source + reOR.source + re3.source + reOR.source + re4.source + reOR.source + re5.source + reOR.source + re6.source + reOR.source + re7.source, 'i');

    return this.formlyService.anInput(filter.name, this.formatLabel(filter))
      .withLabelClass(this.labelClass)
      .withValueClass(this.valueClass)
      .withWrapperClass('col-md-6')
      .withPattern(validateRegex)
      .build();
  }

  private createTextFilter(filter:IColumnMetaData):IExtraFieldConfigurationObject {
    return this.formlyService.anInput(filter.name, this.formatLabel(filter))
      .withLabelClass(this.labelClass)
      .withValueClass(this.valueClass)
      .withWrapperClass('col-md-6')
      .build();
  }

  private createListFilter(filter:IColumnMetaData, isMultiple?:boolean):IExtraFieldConfigurationObject {
    const select: IExtraFieldConfigurationObject = this.formlyService.aNgSelect(filter.name, this.formatLabel(filter))
      .withWrapperClass('col-md-6')
      .withLabelClass(this.labelClass)
      .withValueClass(this.valueClass)
      .withLabelProp('value')
      .withValueProp('id')
      .withOptions([])
      .withExtraOptions({
        templateOptions: {
          addInitialOption: false, // this is to prevent value being overwritten by previous value, because options is not finished loading
          localSearch: isMultiple,
          multiple: isMultiple
        }
      })
      .build();

    if (filter.domain) {
      this.formlyService.fillField(select, this.lovService.list(filter.domain));
    }

    // this is for multiselect because value is not in array when it's on first load
    if (isMultiple && (this.model[filter.name] && !_.isArray(this.model[filter.name]))) {
      setTimeout(() => {
        this.form.controls[filter.name].setValue(this.model[filter.name].split(','));
      });
    }

    return select;
  }

  private createSearchFilter(reportId:string|number, filter:IColumnMetaData, filterValue:string):IExtraFieldConfigurationObject {
    const url:string = `rest/reports/${reportId}/columns/${filter.id}/list`;

    const search:IExtraFieldConfigurationObject = this.formlyService.aSearch(filter.name, this.formatLabel(filter))
      .withDisabled(!!filterValue)
      .withLoading(!!filterValue)
      .withWrapperClass('col-md-6')
      .withLabelClass(this.labelClass)
      .withValueClass(this.valueClass)
      .withValueProp('id')
      .withLabelProp('value')
      .withUrl(url)
      .withExtraOptions({
        templateOptions: {
          getValueRemotely: (value: any): Promise<IFilterOption> => {
            return this.reportDataService.loadListFilterOption(reportId, filter.id, value);
          }
        }
      })
      .build();

    if (filterValue && !_.isObject(filterValue)) {
      // update initial search value
      this.reportDataService.loadListFilterOption(reportId, filter.id, filterValue)
        .then(listOption => {
          search.formControl.setValue(listOption);
        })
        .finally(() => {
          search.templateOptions.disabled = false;
          search.templateOptions.loading = false;
        });
    }

    return search;
  }

  private fillFields(groupCodes?:string[]):void {
    this.setFiltersBusy(true, groupCodes);
    this.reportDataService.loadAllListFilterOptions(this.reportMetadata?.id, groupCodes)
      .then((res:IReportFiltersOption) => {
        _.each(res, (options:IFilterOption[], columnName:string) => {
          const fields:IExtraFieldConfigurationObject[] = this.hasFilterGroups? _.chain(this.fields).values().flatten().value() : <IExtraFieldConfigurationObject[]>this.fields;

          _.chain(fields)
            .filter((field:IExtraFieldConfigurationObject) => field.key === columnName)
            .each((field:IExtraFieldConfigurationObject) => {
              this.formlyService.fillField(field, Promise.resolve(options));
            })
            .value();
        });
      })
      .finally(() => {
        this.setFiltersBusy(false, groupCodes);
      });
  }

  private setFiltersBusy(isBusy:boolean, groupCodes:string[]):void {
    const setFieldsBusy = (fields: IExtraFieldConfigurationObject[]):void => {
      _.chain(fields)
        .filter((field:IExtraFieldConfigurationObject) => field.type === FormlyType.NG_SELECT)
        .each((field: IExtraFieldConfigurationObject) => {
          field.templateOptions.loading = isBusy;
          field.templateOptions.disabled = isBusy;
        })
        .value();
    };

    if (groupCodes?.length) {
      _.chain(groupCodes)
        .map(code => this.fields[code])
        .each(setFieldsBusy)
        .value();
    } else {
      setFieldsBusy(<IExtraFieldConfigurationObject[]>this.fields); // Update fields directly if no group codes
    }
  }

  private validateGroupFilters(groupCode?:string):boolean {
    const group:IFilterGroup = _.findWhere(this.reportMetadata.filterGroups, { code: groupCode });
    // Filtered out columns that don't have a domain. If the array is empty, it means all filters within the group are domain filters.
    return !!_.filter(this.reportMetadata.columns, (col:IColumnMetaData) => group.columns.indexOf(col.name) > -1 && !col.domain)?.length;
  }

  // use formattedLabel when available
  private formatLabel(filter: IColumnMetaData):string {
    return filter && (filter.formattedLabel || filter.label);
  }
}